import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { AppShell } from '@/components/AppShell';
import { InfiniteFeed } from '@/components/feed';
import { Composer } from '@/components/Composer';
import { NoteCard } from '@/components/note';
import { useEventById, useThreadChildren, useAncestors } from '@/hooks/useFeeds';
import { useSocial } from '@/contexts/SocialContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { NoteContent } from '@/components/NoteContent';
import { UserAvatar, Username, Timestamp } from '@/components/user';
import { NoteActions } from '@/components/note';

function decodeId(identifier: string): { id?: string; relays?: string[] } {
  try {
    const decoded = nip19.decode(identifier);
    if (decoded.type === 'note') return { id: decoded.data };
    if (decoded.type === 'nevent') return { id: decoded.data.id, relays: decoded.data.relays };
    return {};
  } catch {
    return /^[0-9a-f]{64}$/i.test(identifier) ? { id: identifier.toLowerCase() } : {};
  }
}

export function NotePage({ identifier }: { identifier: string }) {
  const navigate = useNavigate();
  const { id, relays } = decodeId(identifier);
  const eventQuery = useEventById(id, relays);
  const children = useThreadChildren(id);
  const ancestorsQuery = useAncestors(eventQuery.data);
  const { mutedPubkeys, mutedWords } = useSocial();
  void mutedPubkeys;
  void mutedWords;

  useSeoMeta({
    title: eventQuery.data ? 'Note — Jumble' : 'Note — Jumble',
    description: eventQuery.data?.content.slice(0, 160) ?? 'View a note on Nostr.',
  });

  const [showComposer, setShowComposer] = useState(false);

  return (
    <AppShell
      title="Note"
      right={
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
      }
    >
      {eventQuery.isLoading && (
        <div className="flex gap-3 p-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2"><Skeleton className="h-4 w-32" /><Skeleton className="h-20 w-full" /></div>
        </div>
      )}
      {eventQuery.isError && (
        <div className="p-8 text-center">
          <p className="font-semibold">Note not found</p>
          <p className="text-sm text-muted-foreground">It may have been deleted or the relays don't have it.</p>
        </div>
      )}
      {eventQuery.data && (
        <>
          {(ancestorsQuery.data ?? []).map((a) => (
            <NoteCard key={a.id} event={a} />
          ))}
          <div className="border-b p-4">
            <div className="flex gap-3">
              <UserAvatar pubkey={eventQuery.data.pubkey} size="lg" />
              <div className="min-w-0 flex-1">
                <Username pubkey={eventQuery.data.pubkey} />
                <Timestamp createdAt={eventQuery.data.created_at} className="block text-xs" />
              </div>
            </div>
            <NoteContent event={eventQuery.data} className="mt-3 text-lg leading-relaxed break-words" />
            <NoteActions event={eventQuery.data} detail />
            <Button variant="outline" size="sm" className="mt-3 w-full rounded-full" onClick={() => setShowComposer((v) => !v)}>
              Reply
            </Button>
            {showComposer && (
              <div className="mt-2 rounded-xl border">
                <Composer replyTo={{ id: eventQuery.data.id, pubkey: eventQuery.data.pubkey }} onPublished={() => { setShowComposer(false); children.refetch(); }} autoFocus />
              </div>
            )}
          </div>
          <div className="border-b px-4 py-2 text-sm font-bold">Replies</div>
          {children.isLoading ? (
            <div className="p-4"><Skeleton className="h-16 w-full" /></div>
          ) : (children.data?.replies.length ?? 0) === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No replies yet. Be the first!</p>
          ) : (
            (children.data?.replies ?? []).map((r) => <NoteCard key={r.id} event={r} />)
          )}
          <div className="pb-20 md:pb-4" />
        </>
      )}
    </AppShell>
  );
}

